import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, Pencil, Check, X, Trash } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from '@/hooks/use-toast';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';

interface SimpleChatHeaderProps {
  title: string;
  modelName?: string;
  onTitleChange: (newTitle: string) => void;
  onDeleteChat: () => void;
  projectId?: string | null; // Add this prop
}

const SimpleChatHeader: React.FC<SimpleChatHeaderProps> = ({
  title,
  modelName,
  onTitleChange,
  onDeleteChat,
  projectId // Destructure the new prop
}) => {
  const navigate = useNavigate();
  const [isEditing, setIsEditing] = useState(false);
  const [newTitle, setNewTitle] = useState(title);

  const handleBack = () => {
    // Instead of directly navigating, use setTimeout to prevent freezing
    // This gives React time to clean up before navigation
    setTimeout(() => {
      if (projectId) { // Check if projectId exists
        navigate(`/projects/${projectId}`); // Navigate to project detail
      } else {
        navigate('/'); // Default navigation
      }
    }, 0);
  };
  
  const startEditing = () => {
    setNewTitle(title);
    setIsEditing(true);
  };
  
  const saveTitle = () => {
    if (newTitle.trim() === '') {
      setNewTitle(title);
      toast({
        title: "Title cannot be empty",
        description: "Please enter a valid title for your chat.",
        variant: "destructive"
      });
      return;
    }
    
    onTitleChange(newTitle);
    setIsEditing(false);
  };
  
  const cancelEditing = () => {
    setNewTitle(title);
    setIsEditing(false);
  };
  
  const handleDeleteChat = async () => {
    onDeleteChat();
    navigate('/');
  };

  return (
    <div className="bg-background z-10 border-b px-4 py-3 flex items-center justify-between">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={handleBack} className="shrink-0">
          <ChevronLeft className="h-5 w-5" />
        </Button>
        
        {isEditing ? (
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
              className="text-lg font-medium bg-background border-b-2 border-primary outline-none px-1"
              autoFocus
              onKeyDown={(e) => e.key === 'Enter' && saveTitle()}
              data-testid="chat-title-input"
            />
            <Button variant="ghost" size="sm" onClick={saveTitle} className="h-8 w-8 p-0">
              <Check className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="sm" onClick={cancelEditing} className="h-8 w-8 p-0">
              <X className="h-4 w-4" />
            </Button>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <h1 className="text-lg font-medium truncate" data-testid="chat-title">
              {title || "New Chat"}
            </h1>
            <Button
              variant="ghost" 
              size="sm"
              onClick={startEditing}
              className="h-7 w-7 p-0"
              data-testid="edit-title-button"
            > {/* Changed to opening tag */}
              <Pencil className="h-3.5 w-3.5" />
            </Button> {/* Added closing tag */}
          </div>
        )}
        
        {/* Display model name if provided */}
        {modelName && (
          <div className="hidden md:flex text-xs px-2 py-1 bg-muted rounded-full">
            {modelName}
          </div>
        )}
      </div>
      
      <div className="flex items-center gap-2">
      <AlertDialog>
        <AlertDialogTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className="text-destructive hover:text-destructive hover:bg-destructive/10"
          >
            <Trash className="h-4 w-4 mr-1" />
            <span className="hidden sm:inline">Delete Chat</span>
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete your chat
              and remove its data from our servers.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteChat} // 👈 this will be called by the parent
            >
              Continue
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      </div>
    </div>
  );
};

export default SimpleChatHeader;